import type { RepoTypes } from '#lib/repo.js'
import { differenceBy, sum, sumBy } from '#lib/utils.js'

export type DiscountCode = {
  id: number
  title: string
  code: string
  actions: DiscountAction[]
}

export function calculate(
  cartSeats: Record<number, number>,
  discountCodes: DiscountCode[],
  performance:
    | undefined
    | RepoTypes['Performance']['getWithSeatingChart']
    | RepoTypes['Performance']['forCheckout'],
  {
    vouchers = [],
    voucherFee = 0,
    shippingFee = 0,
    paymentFee = 0,
    bookingFee = 0,
    redeemingVouchers = [],
  }: {
    vouchers: { value: number }[]
    voucherFee: number
    shippingFee?: number
    paymentFee?: number
    bookingFee?: number
    redeemingVouchers?: RedeemingVoucher[]
  },
) {
  const seats = getSeatsFromOrder(cartSeats, performance)
  const seatsPriceSum = getSumFromSeats(seats)

  const discountDifferences = calculateDiscountCodeDifferences(
    discountCodes,
    seats,
  )
  const discountDifference = sumBy(discountDifferences, 'value')
  const bookingFeeTotal = calculateBookingFee(
    seatsPriceSum + discountDifference,
    bookingFee,
  )

  const vouchersSum = vouchers.reduce((sum, voucher) => sum + voucher.value, 0)
  const vouchersFee = voucherFee * vouchers.length

  const total =
    seatsPriceSum +
    shippingFee +
    paymentFee +
    bookingFeeTotal +
    discountDifference +
    vouchersSum +
    vouchersFee

  const voucherDifferences = calculateVoucherDifferences(
    total,
    redeemingVouchers,
  )
  const voucherDifference = sumBy(voucherDifferences, 'difference')

  const leftToPay = total - voucherDifference

  return {
    total,
    leftToPay,
    sum: seatsPriceSum + vouchersSum,
    vouchersFee,
    voucherFee,
    shippingFee,
    paymentFee,
    bookingFeeTotal,
    discountDifferences,
    discountDifference,
    voucherDifferences,
    voucherDifference,
  }
}
export type Prices = ReturnType<typeof calculate>

export function calculateBookingFee(sum: number, bookingFee: number) {
  return Math.round(sum * (bookingFee / 100))
}

export function getSeatsFromOrder(
  orderSeats: Record<number, number>,
  performance:
    | undefined
    | Pick<
        RepoTypes['Performance']['getWithSeatingChart'],
        'seats' | 'categories'
      >,
) {
  if (!performance || !performance.seats) return []
  const seats = performance.seats.filter((seat) => orderSeats[seat.id])

  return seats.map((seat) => {
    const priceCategoryId = orderSeats[seat.id]
    const category = performance.categories.find(
      (category) => category.id === seat.seatCategoryId,
    )
    if (!category) {
      throw new Error(`Seat Category "${seat.seatCategoryId}" is invalid`)
    }

    const priceCategory = category.priceCategories.find(
      (priceCategory) => priceCategory.id === priceCategoryId,
    )
    if (!priceCategory) {
      throw new Error(`PriceCategory "${priceCategoryId}" could not be found`)
    }

    return {
      ...seat,
      category,
      priceCategory,
    }
  })
}

export function getSumFromSeats(seats: Seat[]) {
  return sumBy(seats, (seat) => Number(seat.priceCategory.price))
}

export function calculateDiscountCodeDifferences(
  discountCodes: DiscountCode[],
  seats,
) {
  let visited = []

  return discountCodes.map((discountCode) => {
    const difference = calculateDiscountCodeDifference(
      discountCode.actions,
      seats,
      visited,
    )
    visited = difference.visited

    return {
      id: discountCode.id,
      title: discountCode.title,
      code: discountCode.code,
      value: sum(difference.results),
    }
  })
}

export function calculateDiscountCodeDifference(
  actions: DiscountAction[],
  seats,
  visited,
) {
  const results = actions.map((action) => {
    const potentialSeats = getPotentialSeats(seats, visited)
    const selectedSeats = select(potentialSeats, action)
    visited = visited.concat(selectedSeats)

    return applyAction(selectedSeats, action)
  })

  return {
    results,
    visited,
  }
}

export function getPotentialSeats(
  seats: { id: number }[],
  visited: { id: number }[],
) {
  return differenceBy(seats, visited, (seat) => seat.id)
}

export function select(seats, action?: DiscountAction) {
  let result = seats

  if (!action) return result

  if (action.type === 'NEW_PRICE') {
    result = result.filter((seat) => seat.priceCategory.price > action.value)
  }

  if (action.numberOfTickets) {
    result = result.slice(0, action.numberOfTickets)
  }

  return result
}

export function applyAction(seats, action) {
  if (action.type === 'NEW_PRICE') {
    return action.value * seats.length - getSumFromSeats(seats)
  } else if (action.type === 'NEW_RELATIVE_PRICE') {
    return Math.round(-action.value * getSumFromSeats(seats))
  } else {
    throw new Error('not implemented')
  }
}

export function calculateVoucherDifferences(
  subtotal: number,
  vouchers: RedeemingVoucher[],
) {
  return vouchers.reduce(
    (differences, voucher) => {
      if (subtotal === 0) return differences
      if (subtotal < 0) throw new Error('subtotal is negative')

      const difference = Math.min(subtotal, voucher.remainingValue)
      subtotal -= difference

      return differences.concat({
        id: voucher.id,
        code: voucher.code,
        difference,
      })
    },
    [] as {
      id: number
      code: string
      difference: number
    }[],
  )
}

export function leftToPay(order: { price: number; paidWithVouchers: number }) {
  return order.price - order.paidWithVouchers
}
